<template>
    <div class="container">
        <o-bread-crumb>
            <template v-slot:pageName>Point Management</template>
            <template v-slot:pageDetail>Expiration</template>
        </o-bread-crumb>        
        
        <FeatureGate featureKey="point_control">
        
        <div v-if="validation">
            <div class="alert alert-danger mt-1" role="alert">
                {{ validation }}
            </div>
        </div>

        <!-- Configuration & Overview Section -->
        <div class="card text-center bg-white shadow-none mt-3 mb-4">
            <div class="card-header border-bottom">
                <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
                    <li class="nav-item text-start">
                        <div class="d-flex d-inline-block">
                            <h4 class="header-title">
                                Expiration Configuration
                            </h4>
                        </div>
                        <p class="header-subtitle me-4 mt-1">Manage automatic point expiration rules</p>   
                    </li>
                    <li class="nav-item d-flex align-items-center gap-2">
                        <button class="btn-config-action" @click="showHowItWorks = true" title="Learn how expiration works">
                            <i class="fas fa-info-circle text-primary"></i> <span class="fw-medium">How it works</span>
                        </button>
                        
                        <div class="duration-card cursor-pointer" @click="openConfigModal" title="Click to configure expiration settings">
                            <div class="duration-icon" :class="config.enabled ? 'bg-success-light' : 'bg-secondary-light'">
                                <i class="fas fa-clock" :class="config.enabled ? 'text-success' : 'text-secondary'"></i>
                            </div>
                            <div class="duration-content text-start">
                                <span class="duration-label text-uppercase fw-bold text-muted">Current Duration</span>
                                <h5 class="duration-value m-0 fw-bold">{{ formatDurationText(config.expiration_days) }}</h5>
                            </div>
                            <div class="duration-status">
                                <span class="status-dot" :class="config.enabled ? 'bg-success' : 'bg-secondary'"></span>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
            
            <div class="card-body text-start">
                <div class="row g-3 row-cols-1 row-cols-md-5">
                    <div class="col">
                        <div class="summary-card">
                            <div class="summary-icon">
                                <i class="fas fa-minus-circle"></i>
                            </div>
                            <div class="summary-details">
                                <p class="summary-label">Points Deducted</p>
                                <h3 class="summary-value">{{ formatPrice(summary.total_points_deducted) }}</h3>
                            </div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="summary-card">
                            <div class="summary-icon" style="background-color: #3b82f6;">
                                <i class="fas fa-users"></i>
                            </div>
                            <div class="summary-details">
                                <p class="summary-label">Members Affected</p>
                                <h3 class="summary-value">{{ formatPrice(summary.members_affected) }}</h3>
                            </div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="summary-card">
                            <div class="summary-icon" style="background-color: #f59e0b;">
                                <i class="fas fa-check-double"></i>
                            </div>
                            <div class="summary-details">
                                <p class="summary-label">Transactions Processed</p>
                                <h3 class="summary-value">{{ formatPrice(summary.processed_count || 0) }}</h3>
                            </div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="summary-card" :class="{ 'summary-card--disabled': !summary.point_value_enabled }">
                            <div class="summary-icon" :style="summary.point_value_enabled ? 'background-color: #10b981;' : 'background-color: #94a3b8;'">
                                <i class="fas fa-wallet"></i>
                            </div>
                            <div class="summary-details">
                                <p class="summary-label">Liability Recovery</p>
                                <template v-if="summary.point_value_enabled">
                                    <h3 class="summary-value">IDR {{ formatPrice(summary.liability_recovery) }}</h3>
                                </template>
                                <template v-else>
                                    <div class="point-value-off-notice">
                                        <span class="point-value-off-text">
                                            <i class="fas fa-toggle-off me-1"></i>Point Value is off
                                        </span>
                                        <span class="point-value-off-sub">Enable it to calculate liability</span>
                                        <router-link to="/point-settings" class="btn-enable-point-value">
                                            <i class="fas fa-cog me-1"></i>Point Settings
                                        </router-link>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="summary-card">
                            <div class="summary-icon" style="background-color: #64748b;">
                                <i class="fas fa-clock"></i>
                            </div>
                            <div class="summary-details">
                                <p class="summary-label">Last Processed</p>
                                <h3 class="summary-value">{{ summary.last_processed_at ? dateTimeFormat(summary.last_processed_at) : '-' }}</h3>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="card text-center bg-white shadow-none">
            <div>
                <div class="card-header border-bottom">
                    <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
                        <li class="nav-item text-start">
                            <div class="d-flex d-inline-block">
                                <h4 class="header-title">
                                    Expiration History
                                </h4>
                            </div>
                            <p class="header-subtitle me-4 mt-1">Review all historical point expiration records</p>   
                        </li>
                    </ul>
                </div>
                
                <div class="card-body">
                <div class="spinner" v-if='page.isLoading'></div>
                <div v-if="page.output">
                    <div class="filter-action-wrapper mb-4 mt-3">
                        <div class="filter-controls">
                            <!-- Search Control (Tabs + Input) -->
                            <div class="search-box-custom">
                                <div class="search-type-tabs">
                                    <button 
                                        v-for="option in searchOptions" 
                                        :key="option.value"
                                        class="tab-btn"
                                        :class="{ active: searchBy.selected === option.value }"
                                        @click="selectSearchType(option.value)"
                                    >
                                        {{ option.text }}
                                    </button>
                                </div>
                                <div class="search-input-wrapper">
                                    <input 
                                        type="text" 
                                        class="form-control-search" 
                                        v-model="searchBy.criteria" 
                                        :placeholder="searchPlaceholder" 
                                        @keyup.enter="search()" 
                                    />
                                    <button v-if="!isSearchActive" class="btn-search" @click="search()">
                                        <i class="fas fa-search"></i>
                                    </button>
                                    <button v-else class="btn-search" @click="clearSearch()">
                                        <i class="fas fa-times text-danger"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        <div class="action-section">
                            <div class="action-buttons-group">
                                <button class="btn btn-refresh" @click.prevent="fetchDataAll()" title="Refresh">
                                    <i class="fas fa-sync-alt"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <o-table style="text-align:left;" class="transaction-list-table">
                        <thead>
                            <tr>
                                <th class="col-no">No</th>
                                <th class="col-date">Execution Date</th>
                                <th class="col-member">Member</th>
                                <th class="col-info">Origin Transaction</th>
                                <th class="col-point text-start">Points Expired</th>
                                <th class="col-recovery text-start">Recovery</th>
                                <th class="col-code text-center" style="width: 100px;">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                             <tr v-for="(item, index) in tableData" :key="index">
                                <td class="col-no text-center">{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                                <td class="col-date">
                                    <span class="date-text text-nowrap">{{ dateTimeFormat(item.processed_at || item.created_at) }}</span>
                                </td>
                                <td class="col-member">
                                    <div class="d-flex align-items-center gap-2">
                                        <div class="member-avatar theme-bg" v-if="item.member">
                                            <img v-if="item.member.user?.user_profile?.avatar?.image_url && !item.member.imageError" 
                                                 :src="item.member.user.user_profile.avatar.image_url" 
                                                 @error="item.member.imageError = true"
                                                 alt="avatar">
                                            <span v-else class="text-white">{{ getInitials(item.member) }}</span>
                                        </div>
                                        <div class="member-info-text text-truncate">
                                            <span :class="{ 'text-red': !item.member }" class="fw-medium">
                                                {{ item.member ? item.member.first_name + ' ' + (item.member.last_name || '') : 'Member Deleted' }}
                                            </span>
                                            <br v-if="item.member">
                                            <span class="small text-muted">{{ item.member?.user?.email }}</span>
                                        </div>
                                    </div>
                                </td>
                                <td class="col-info text-truncate">
                                    <div class="d-flex flex-column gap-1">
                                        <span class="fw-medium text-muted small"><i class="fas fa-receipt me-1"></i>{{ item.transaction?.code || '-' }}</span>
                                        <span class="small text-muted">
                                            <i class="fas fa-check-circle me-1 text-success" style="font-size: 9px;" v-if="item.transaction?.materialized_at"></i>Materialize at: {{ item.transaction?.materialized_at ? dateTimeFormat(item.transaction.materialized_at) : dateTimeFormat(item.transaction?.created_at) }}
                                        </span>
                                        <span class="small text-muted" style="font-size: 11px;" v-if="item.transaction?.materialized_at && item.transaction?.materialized_at !== item.transaction?.created_at">
                                            <i class="fas fa-clock me-1" style="font-size: 9px;"></i>Created: {{ dateTimeFormat(item.transaction?.created_at) }}
                                        </span>
                                    </div>
                                </td>
                                <td class="col-point text-start">
                                    <div class="d-flex align-items-center justify-content-start gap-1">
                                        <i class="fas fa-coins" :class="item.status === 'failed' || item.status === 'skipped' ? 'text-muted' : 'text-danger'"></i>
                                        <span class="point-value fw-bold" :class="item.status === 'failed' || item.status === 'skipped' ? 'text-muted' : 'text-danger'">
                                            -{{ item.status === 'failed' || item.status === 'skipped' ? '0' : formatPrice(item.points_to_expire) }}
                                        </span>
                                    </div>
                                </td>
                                <td class="col-recovery text-start">
                                    <span v-if="summary.point_value_enabled && item.status !== 'failed' && item.status !== 'skipped'" class="fw-medium small recovery-text">
                                        IDR {{ formatPrice((item.points_to_expire || 0) * (summary.point_value_rate || 0)) }}
                                    </span>
                                    <span v-else-if="item.status === 'failed' || item.status === 'skipped'" class="text-muted small">-</span>
                                    <span v-else-if="!summary.point_value_enabled" class="text-muted small fst-italic"><i class="fas fa-toggle-off me-1" title="Point Value is disabled"></i>—</span>
                                    <span v-else class="text-muted small fst-italic">N/A</span>
                                </td>
                                <td class="col-code text-center">
                                    <span class="status-badge" :class="getStatusBadgeClass(item.status)" :title="item.failure_reason || ''">
                                        {{ getStatusLabel(item.status) }}
                                        <i v-if="item.failure_reason" class="fas fa-exclamation-circle ms-1" style="font-size: 9px;"></i>
                                    </span>
                                </td>
                            </tr>
                            <tr v-if="tableData.length === 0">
                                <td colspan="7" class="text-center py-4 text-muted">No expiration history found.</td>
                            </tr>
                        </tbody>
                    </o-table>
                </div>
            </div>

            <div class="container mt-4 mb-4">
                <o-pagination :page="meta.page" :total="meta.total" @callback="navPage" />
            </div>
            
        </div>
        </div>

        <!-- Setup Configuration Modal -->
        <div v-if="showConfigModal" class="modal-backdrop" @click.self="cancelConfigModal" style="z-index: 1050; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center;">
            <div class="card shadow-lg" style="width: 90%; max-width: 480px; animation: slideIn 0.3s ease-out; background: white">
                <div class="card-header border-bottom p-4 d-flex justify-content-between align-items-center">
                    <h5 class="m-0 fw-bold text-dark"><i class="fas fa-cog text-primary me-2"></i>Expiration Configuration</h5>
                    <button class="btn-close" @click="cancelConfigModal"></button>
                </div>
                <div class="card-body p-4 text-start bg-light">
                    <!-- Toggle active -->
                    <div class="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm mb-4 border">
                        <div>
                            <span class="fw-bold d-block text-dark">Feature Status</span>
                            <span class="small text-muted">{{ pendingConfig.enabled ? 'Automated point expiration is active' : 'Automated point expiration is paused' }}</span>
                        </div>
                        <div class="form-check form-switch custom-switch mb-0">
                            <input 
                                class="form-check-input mb-0 mt-0" 
                                type="checkbox" 
                                v-model="pendingConfig.enabled"
                                style="cursor: pointer; width: 44px; height: 24px;"
                            >
                        </div>
                    </div>

                    <!-- Duration Input -->
                    <div class="form-group mb-4">
                        <label class="fw-bold text-dark mb-2">Duration Before Expiration</label>
                        <div class="input-group">
                            <input 
                                type="number" 
                                class="form-control" 
                                v-model.number="pendingConfig.expiration_days"
                                min="1"
                                max="3650"
                                placeholder="e.g., 365"
                            />
                            <span class="input-group-text bg-white">Days</span>
                        </div>
                    </div>

                    <!-- Dynamic Summary Info -->
                    <div class="alert alert-info d-flex align-items-start gap-3 m-0" style="border-radius: 12px;">
                        <i class="fas fa-info-circle fs-4 text-info mt-1"></i>
                        <div>
                            <h6 class="fw-bold text-dark mb-1">Impact Analysis</h6>
                            <p class="mb-0 text-dark small" style="line-height: 1.5;">
                                With the duration set to <strong>{{ pendingConfig.expiration_days || 0 }} days</strong>, points acquired before 
                                <strong class="text-danger">{{ calculateTargetDate(pendingConfig.expiration_days) }}</strong> 
                                will expire when processed.
                            </p>
                        </div>
                    </div>
                </div>
                <div class="card-footer p-4 bg-white text-end border-top d-flex justify-content-end gap-2" style="border-bottom-left-radius: 16px; border-bottom-right-radius: 16px;">
                    <button class="btn btn-outline-secondary m-0" @click="cancelConfigModal" :disabled="isSavingConfig">Cancel</button>
                    <button class="btn btn-primary m-0 text-white" @click="saveConfigModal" :disabled="isSavingConfig">
                        <div v-if="isSavingConfig" class="spinner-border spinner-border-sm me-1 text-white"></div>
                        Save Configuration
                    </button>
                </div>
            </div>
        </div>

        <!-- How it Works Modal (Clean Two-Column Layout) -->
        <div v-if="showHowItWorks" class="modal-backdrop" @click.self="showHowItWorks = false" style="z-index: 1050; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(15, 23, 42, 0.4); display: flex; align-items: center; justify-content: center;">
            <div class="card" style="width: 95%; max-width: 800px; animation: slideIn 0.3s ease-out; background: white; border-radius: 12px; overflow: hidden; border: none; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);">
                <div class="card-header border-bottom d-flex justify-content-between align-items-center p-4" style="background: white;">
                    <h5 class="m-0 fw-bold text-dark"><i class="fas fa-info-circle text-primary me-2"></i>How It Works</h5>
                    <button class="btn-close" @click="showHowItWorks = false"></button>
                </div>
                <div class="card-body p-4 text-start bg-white">
                    <div class="row gx-5">
                        <!-- Left Column: Explanation -->
                        <div class="col-md-6 mb-4 mb-md-0">
                            <div class="mb-4">
                                <strong style="font-size: 13px; color: #1e293b;">1. Set Lifespan:</strong><br>
                                <span class="text-muted" style="font-size: 13px;">Define the maximum age (in days) a point is allowed to stay unused before it expires automatically.</span>
                            </div>
                            <div class="mb-4">
                                <strong style="font-size: 13px; color: #1e293b;">2. Automated Checks:</strong><br>
                                <span class="text-muted" style="font-size: 13px;">The system checks members' point balances every day in the background, identifying points that have crossed the lifespan threshold.</span>
                            </div>
                            <div class="mb-0">
                                <strong style="font-size: 13px; color: #1e293b;">3. FIFO (First-In, First-Out):</strong><br>
                                <span class="text-muted" style="font-size: 13px;">When members spend points, the oldest points are always consumed first. Only the unspent leftovers will expire.</span>
                            </div>
                        </div>
                        
                        <!-- Right Column: Example Case -->
                        <div class="col-md-6">
                            <div class="scenario-box shadow-sm">
                                <div class="scenario-header">
                                    <span class="scenario-title">Example Scenario</span>
                                    <span class="scenario-config">
                                        <i class="fas fa-clock text-primary me-1" style="font-size: 10px;"></i>
                                        Lifespan: <strong>365 Days</strong>
                                    </span>
                                </div>
                                
                                <div class="scenario-timeline">
                                    <!-- Event 1 -->
                                    <div class="timeline-step success">
                                        <div class="timeline-marker"></div>
                                        <div class="step-content">
                                            <span class="step-date">Jan 1, 2024</span>
                                            <span class="step-desc">Member earns <strong class="text-success">100 Pts</strong></span>
                                        </div>
                                    </div>
                                    
                                    <!-- Event 2 -->
                                    <div class="timeline-step info">
                                        <div class="timeline-marker" style="border-color: #3b82f6;"></div>
                                        <div class="step-content">
                                            <span class="step-date">Jun 1, 2024</span>
                                            <span class="step-desc">Member redeems <strong class="text-danger">40 Pts</strong></span>
                                            <div class="step-note">
                                                <i class="fas fa-calculator me-1 opacity-75"></i>
                                                Leftover: 60 Pts
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <!-- Event 3 -->
                                    <div class="timeline-step danger">
                                        <div class="timeline-marker"></div>
                                        <div class="step-content">
                                            <span class="step-date">Jan 1, 2025</span>
                                            <span class="step-desc">Remaining <strong class="text-danger fw-bold">60 Pts</strong> expires automatically.</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </FeatureGate>
    </div>
</template>

<script>
import OTable from "@/components/OmnihControl/OTable.vue";
import OPagination from "@/components/OmnihControl/OPagination.vue";
import OBreadCrumb from "@/components/OmnihControl/OBreadCrumb.vue";
import FeatureGate from '@/components/upsell/FeatureGate.vue';
import PointExpirationService from "@/services/PointExpirationService";

export default {
    name: "PointExpiration",
    components: {
        OTable,
        OPagination,
        OBreadCrumb,
        FeatureGate,
    },
    data() {
        return {
            validation: "",
            page: {
                isLoading: false,
                output: true,
            },
            isSavingConfig: false,
            config: {
                enabled: false,
                expiration_days: 365
            },
            summary: {
                total_points_deducted: 0,
                members_affected: 0,
                liability_recovery: 0,
                point_value_enabled: false,
                point_value_rate: 0,
                last_processed_at: null
            },
            tableData: [],
            meta: {
                page: 1,
                limit: 10,
                total: 1
            },
            searchOptions: [
                { text: 'Member', value: 'member_name' },
                { text: 'Transaction', value: 'transaction_code' }
            ],
            searchBy: {
                selected: 'member_name',
                criteria: ""
            },
            isSearchActive: false,
            
            showConfigModal: false,
            pendingConfig: {
                enabled: false,
                expiration_days: 365
            },
            showHowItWorks: false,
        };
    },
    computed: {
        corporateId() {
            // Assuming we use corporate\_id from vuex store or local storage
            return this.$store.state.Auth.selectedBusinessProfile?.corporate_id || localStorage.getItem('corporate_id');
        },
        searchPlaceholder() {
            return this.searchBy.selected === 'member_name' 
                ? 'Search by member name or email...' 
                : 'Search by origin transaction code...';
        }
    },
    mounted() {
        this.fetchDataAll();
    },
    methods: {
        async fetchDataAll() {
            if (!this.corporateId) return;
            this.validation = "";
            this.page.isLoading = true;
            try {
                // Fetch Config
                const configRes = await PointExpirationService.getConfig(this.corporateId);
                if (configRes.data && configRes.data.status) {
                    this.config = configRes.data.data;
                }

                // Fetch Summary
                const summaryRes = await PointExpirationService.getSummary(this.corporateId);
                if (summaryRes.data && summaryRes.data.status) {
                    this.summary = summaryRes.data.data;
                }

                // Fetch History
                await this.fetchHistory();

            } catch (error) {
                console.error("Error fetching point expiration data", error);
                this.validation = "Failed to load point expiration data. Please try again.";
            } finally {
                this.page.isLoading = false;
            }
        },
        async fetchHistory() {
            try {
                const params = {
                    meta: {
                        page: this.meta.page,
                        limit: this.meta.limit
                    }
                };

                if (this.isSearchActive && this.searchBy.criteria) {
                    params.search = {
                        [this.searchBy.selected]: this.searchBy.criteria
                    };
                }

                const res = await PointExpirationService.getHistory(this.corporateId, params);
                if (res.data && res.data.status) {
                    this.tableData = res.data.data;
                    this.meta.total = res.data.meta.total;
                }
            } catch (error) {
                console.error("Error fetching history", error);
                throw error; // Let the caller handle it
            }
        },
        async saveConfigModal() {
            if (this.isSavingConfig) return;
            const days = parseInt(this.pendingConfig.expiration_days);
            if (isNaN(days) || days < 1) {
                this.validation = "Please enter a valid duration.";
                return;
            }

            this.isSavingConfig = true;
            this.validation = "";

            try {
                const data = {
                    enabled: this.pendingConfig.enabled,
                    expiration_days: days
                };
                
                await PointExpirationService.updateConfig(this.corporateId, data);
                
                // Update local visual state directly to avoid unnecessary re-fetch of table if unchanged
                this.config.enabled = data.enabled;
                this.config.expiration_days = data.expiration_days;
                this.showConfigModal = false;
                
            } catch (error) {
                console.error("Error saving config", error);
                this.validation = "Failed to update configuration. Please check your inputs.";
            } finally {
                this.isSavingConfig = false;
            }
        },
        openConfigModal() {
            this.pendingConfig = { 
                enabled: this.config.enabled, 
                expiration_days: this.config.expiration_days || 365
            };
            this.showConfigModal = true;
        },
        cancelConfigModal() {
            if (this.isSavingConfig) return;
            this.showConfigModal = false;
        },
        formatDurationText(days) {
            if (!days) return "0 Month, 0 Day";
            const months = Math.floor(days / 30);
            const rDays = days % 30;
            return `${months} Month, ${rDays} Day`;
        },
        calculateTargetDate(days) {
            days = parseInt(days) || 0;
            if (days < 1) return "-";
            const target = new Date();
            target.setDate(target.getDate() - days);
            return target.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
        },
        selectSearchType(type) {
            this.searchBy.selected = type;
            this.clearSearch();
        },
        search() {
            if (this.searchBy.criteria.trim() === "") return;
            this.isSearchActive = true;
            this.meta.page = 1;
            this.page.isLoading = true;
            this.fetchHistory().finally(() => {
                this.page.isLoading = false;
            });
        },
        clearSearch() {
            this.searchBy.criteria = "";
            this.isSearchActive = false;
            this.meta.page = 1;
            this.page.isLoading = true;
            this.fetchHistory().finally(() => {
                this.page.isLoading = false;
            });
        },
        navPage(page) {
            this.meta.page = page;
            this.page.isLoading = true;
            this.fetchHistory().finally(() => {
                this.page.isLoading = false;
            });
        },
        dateTimeFormat(dateStr) {
            if (!dateStr) return '-';
            const date = new Date(dateStr);
            if (isNaN(date)) return '-';
            
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const day = String(date.getDate()).padStart(2, '0');
            const month = months[date.getMonth()];
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            
            return `${day} ${month} ${year}, ${hours}:${minutes}`;
        },
        formatPrice(value) {
            let val = (value / 1).toFixed(0).replace(".", ",");
            return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        },
        getInitials(member) {
            if (!member || !member.first_name) return "U";
            return member.first_name.charAt(0).toUpperCase();
        },
        /**
         * Get CSS class for status badge based on expiration status.
         * - processed: green (success)
         * - pending: yellow (in queue)
         * - skipped: grey (valid business skip, not an error)
         * - failed: red (actual system error)
         */
        getStatusBadgeClass(status) {
            const classMap = {
                'processed': 'after-tax',
                'pending': 'before-tax',
                'skipped': 'skipped-status',
                'failed': 'expired',
            };
            return classMap[status] || 'before-tax';
        },
        /**
         * Get human-readable label for expiration status
         */
        getStatusLabel(status) {
            const labelMap = {
                'processed': 'Processed',
                'pending': 'Pending',
                'skipped': 'Skipped',
                'failed': 'Error',
            };
            return labelMap[status] || status.charAt(0).toUpperCase() + status.slice(1);
        }
    }
};
</script>

<style scoped>

.btn-config-action {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 11px 14px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    color: #475569;
    font-size: 13px;
    transition: all 0.2s ease;
}

.btn-config-action:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #1e293b;
}

/* Duration Card styling */
.duration-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 14px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    transition: all 0.2s ease;
    user-select: none;
}

.duration-card:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
}

.cursor-pointer {
    cursor: pointer;
}

.duration-icon {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
}

.bg-success-light {
    background-color: #e6f8ec;
}

.bg-secondary-light {
    background-color: #f1f3f5;
}

.duration-content {
    display: flex;
    flex-direction: column;
}

.duration-label {
    font-size: 10px;
    letter-spacing: 0.5px;
    color: #64748b;
}

.duration-value {
    color: #0f172a;
    font-size: 13px;
    line-height: 1.2;
}

.duration-status {
    margin-left: 4px;
}

.status-dot {
    display: block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

@keyframes slideIn {
    from {
        transform: translateY(-20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

/* Card Headers */
.header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}

.header-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
}

/* Summary Cards */
.total-summary {
    padding: 0 10px;
}

.summary-card {
    background: white;
    border: 1px solid #eef2f7;
    border-radius: 12px;
    padding: 24px;
    transition: none;
    height: 100%;
}

.summary-card:hover {
    transform: none;
    box-shadow: none;
}

.summary-card--disabled {
    background: #f8fafc;
    border-color: #e2e8f0;
    opacity: 0.85;
}

/* Point Value Off Notice (inside Liability Recovery card) */
.point-value-off-notice {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.point-value-off-text {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
}

.point-value-off-sub {
    font-size: 11px;
    color: #94a3b8;
    line-height: 1.4;
}

.btn-enable-point-value {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    padding: 5px 10px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    color: #475569;
    font-size: 11px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.2s;
    width: fit-content;
}

.btn-enable-point-value:hover {
    background: #e2e8f0;
    color: #1e293b;
    border-color: #cbd5e1;
    text-decoration: none;
}

.summary-details {
    flex: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 16px;
}

.summary-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background-color: var(--primary-color);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 18px;
    flex-shrink: 0;
}

.summary-label {
    font-size: 14px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
    text-transform: none;
    letter-spacing: normal;
}

.summary-value {
    font-size: 14px;
    font-weight: 500;
    color: #64748b;
    margin: 0;
    line-height: 1.4;
}

/* Table Styling Improvements */
.transaction-list-table {
    white-space: nowrap;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
}

.transaction-list-table th,
.transaction-list-table td {
    padding: 16px 20px !important;
    vertical-align: middle;
}

.transaction-list-table tbody tr:hover,
.transaction-list-table tbody tr:hover td,
.transaction-list-table tbody tr:hover * {
    transform: none !important;
    transition-duration: 0ms !important;
}

/* Explicit Widths for Clean Column Spacing */
.col-no { width: 60px; }
.col-date { width: 180px; }
.col-member { min-width: 200px; max-width: 250px; }
.col-info { min-width: 180px; max-width: 220px; }
.col-point { width: 150px; }
.col-recovery { width: 160px; }
.col-code { width: 110px; }

.date-text {
    font-size: 13px;
    color: #4a5568;
    white-space: nowrap;
}

/* Filter and Action Bar Styling */
.filter-action-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-end; 
    gap: 20px;
    flex-wrap: wrap;
    background-color: #f8fafc;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.filter-section, .action-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.filter-controls {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

/* Custom Search Box */
.search-box-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 1 350px;
    min-width: 250px;
}

.search-type-tabs {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
}

.tab-btn {
    border: none;
    background: transparent;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
    border-radius: 6px;
    transition: all 0.2s;
    cursor: pointer;
}

.tab-btn.active {
    background: white;
    color: var(--primary-color);
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    font-weight: 600;
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 4px;
    transition: all 0.2s;
}

.search-input-wrapper:focus-within {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.1);
}

.search-input-wrapper input {
    border: none;
    padding: 8px 12px;
    font-size: 14px;
    width: 100%;
    outline: none;
    background: transparent;
    color: #334155;
}

.btn-search {
    background: #f8fafc;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-search:hover {
    background: #e2e8f0;
    color: var(--primary-color);
}

.action-buttons-group {
    display: flex;
    align-items: center;
    gap: 10px;
}

.btn-refresh {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    border-radius: 6px;
}

.btn-refresh:hover {
    background-color: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
}

/* Status Badges */
.status-badge {
    padding: 2px 8px;
    border-radius: 5px;
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    display: inline-block;
    white-space: nowrap;
    border: none;
}

.status-badge.active {
    background-color: #f0f9ff;
    color: #0c4a6e;
    border: 1px solid #bae6fd;
}

.status-badge.before-tax {
    background-color: #fffbeb;
    color: #92400e;
    border: 1px solid #fef3c7;
}

.status-badge.after-tax {
    background-color: #f5f3ff;
    color: #4338ca;
    border: 1px solid #ddd6fe;
}

.status-badge.expired {
    background-color: #fff1f2;
    color: #9f1239;
    border: 1px solid #fecdd3;
}

.status-badge.skipped-status {
    background-color: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
}

/* Base Badge Alignment Fix */
.info-tooltip-wrapper {
    display: flex;
    align-items: center;
    color: #94a3b8;
    transition: color 0.2s;
}

.info-tooltip-wrapper:hover {
    color: #64748b;
}

.text-theme {
    color: #f87171 !important;
}

.recovery-text {
    color: #10b981 !important;
}

.product-name-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.4;
    font-size: 13px;
    color: #334155;
    font-weight: 500;
}

/* Member Avatar */
.member-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #e2e8f0;
    color: #475569;
    font-weight: 700;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
}

.member-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.fw-medium {
    font-weight: 500;
}

.theme-bg {
    background-color: #f87171 !important;
}

.text-white {
    color: #ffffff !important;
}

/* Example Scenario Timeline */
.scenario-box {
    background-color: #f8fafc;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    padding: 24px;
    height: 100%;
}

.scenario-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 14px;
    border-bottom: 1px dashed #cbd5e1;
}

.scenario-title {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.scenario-config {
    font-size: 12px;
    color: #1e293b;
    background: white;
    padding: 4px 10px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.scenario-timeline {
    position: relative;
    padding-left: 20px;
}

.scenario-timeline::before {
    content: '';
    position: absolute;
    left: 7px;
    top: 5px;
    bottom: 5px;
    width: 2px;
    background: #e2e8f0;
}

.timeline-step {
    position: relative;
    margin-bottom: 24px;
}

.timeline-step:last-child {
    margin-bottom: 0;
}

.timeline-marker {
    position: absolute;
    left: -20px;
    top: 4px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    border: 3px solid #cbd5e1;
    z-index: 1;
}

.timeline-step.success .timeline-marker { border-color: #10b981; }
.timeline-step.danger .timeline-marker { border-color: #ef4444; }

.step-content {
    display: flex;
    flex-direction: column;
}

.step-date {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    margin-bottom: 2px;
}

.step-desc {
    font-size: 13px;
    color: #1e293b;
    font-weight: 500;
}

.step-note {
    font-size: 11px;
    color: #475569;
    background: #f1f5f9;
    padding: 4px 10px;
    border-radius: 6px;
    margin-top: 8px;
    display: flex;
    align-items: center;
    width: max-content;
    font-weight: 600;
    border: 1px solid #e2e8f0;
}
</style>
